import { useEffect, useState } from "react";
import { motion } from "motion/react";
import type { GameKind } from "@minigames/shared";
import { api } from "../lib/api.js";
import { haptic } from "../lib/telegram.js";
import { useStore } from "../lib/store.js";

const GAME_NAME: Record<GameKind, string> = {
  chess: "Шахматы",
  durak: "Дурак",
  checkers: "Шашки",
  reversi: "Реверси",
  gomoku: "Пять в ряд",
  seabattle: "Морской бой",
};

interface RatingRow {
  game: GameKind;
  rating: number;
  deviation: number;
  games: number;
  wins: number;
  losses: number;
  draws: number;
  tier: string;
  tierIcon: string;
  established: boolean;
}

interface Season {
  id: number;
  name: string;
  daysLeft: number;
}

interface Leader {
  userId: string;
  name: string | null;
  photoUrl: string | null;
  rating: number;
  games: number;
  wins: number;
  tier: string;
  tierIcon: string;
}

export function Rating() {
  const { setScreen, me } = useStore();
  const [season, setSeason] = useState<Season | null>(null);
  const [ratings, setRatings] = useState<RatingRow[]>([]);
  const [tab, setTab] = useState<"mine" | GameKind>("mine");
  const [leaders, setLeaders] = useState<Leader[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .rating()
      .then((res) => {
        if (cancelled) return;
        setSeason(res.season as Season);
        setRatings(res.ratings as RatingRow[]);
      })
      .catch((e: unknown) => {
        if (!cancelled) setError(e instanceof Error ? e.message : "не загрузилось");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (tab === "mine") return;
    let cancelled = false;
    setLeaders(null);
    api
      .leaderboard(tab)
      .then((res) => {
        if (!cancelled) setLeaders(res.leaders as Leader[]);
      })
      .catch(() => {
        if (!cancelled) setLeaders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [tab]);

  const played = ratings.reduce((n, r) => n + r.games, 0);

  return (
    <motion.div
      className="stack"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
    >
      <div className="row">
        <button
          className="btn"
          style={{ width: "auto", padding: "8px 14px" }}
          onClick={() => {
            haptic.tap();
            setScreen("lobby");
          }}
        >
          ← Назад
        </button>
        <div style={{ fontWeight: 700, fontSize: 18 }}>Рейтинг</div>
      </div>

      {season && (
        <div className="card row" style={{ justifyContent: "space-between" }}>
          <span>
            <span style={{ display: "block", fontWeight: 700 }}>{season.name}</span>
            <span className="muted small">осталось {season.daysLeft} дн.</span>
          </span>
          <span style={{ fontSize: 26 }}>🏆</span>
        </div>
      )}

      {error && <div className="muted small">{error}</div>}

      {/* Вкладки: мои рейтинги + таблица лидеров по каждой игре */}
      <div className="tabs">
        <button
          className={`tab${tab === "mine" ? " on" : ""}`}
          onClick={() => {
            haptic.select();
            setTab("mine");
          }}
        >
          Мой
        </button>
        {(Object.keys(GAME_NAME) as GameKind[]).map((g) => (
          <button
            key={g}
            className={`tab${tab === g ? " on" : ""}`}
            onClick={() => {
              haptic.select();
              setTab(g);
            }}
          >
            {GAME_NAME[g]}
          </button>
        ))}
      </div>

      {tab === "mine" ? (
        played === 0 ? (
          <div className="card stack" style={{ gap: 8, textAlign: "center" }}>
            <div style={{ fontSize: 34 }}>🌱</div>
            <div style={{ fontWeight: 700 }}>Рейтинга пока нет</div>
            <div className="muted small">
              Сыграй с живым соперником — партии против бота в рейтинг не идут,
              иначе его можно было бы просто нафармить.
            </div>
          </div>
        ) : (
          <div className="stack" style={{ gap: 8 }}>
            {ratings
              .slice()
              .sort((a, b) => b.games - a.games)
              .map((r) => (
                <div key={r.game} className="rating-row">
                  <span className="rating-icon">{r.tierIcon}</span>
                  <span style={{ minWidth: 0, flex: 1 }}>
                    <span style={{ display: "block", fontWeight: 600 }}>
                      {GAME_NAME[r.game] ?? r.game}
                    </span>
                    <span className="muted small">
                      {r.tier} · {r.games} партий · {r.wins}–{r.losses}
                      {r.draws > 0 ? `–${r.draws}` : ""}
                    </span>
                  </span>
                  <span style={{ flex: "none", textAlign: "right" }}>
                    <b style={{ fontSize: 18 }}>{r.rating}</b>
                    {!r.established && (
                      // Пока отклонение велико, число мало что значит —
                      // честнее сказать об этом, чем показывать как факт.
                      <span
                        className="muted"
                        style={{ display: "block", fontSize: 10, lineHeight: 1.2 }}
                      >
                        уточняется
                      </span>
                    )}
                  </span>
                </div>
              ))}
            <div className="muted small" style={{ textAlign: "center" }}>
              Рейтинг становится точным примерно за дюжину партий
            </div>
          </div>
        )
      ) : leaders === null ? (
        <div className="center" style={{ padding: 30 }}>
          <div className="spinner" />
        </div>
      ) : leaders.length === 0 ? (
        <div className="card stack" style={{ gap: 8, textAlign: "center" }}>
          <div style={{ fontSize: 30 }}>🏁</div>
          <div style={{ fontWeight: 700 }}>Таблица пока пуста</div>
          <div className="muted small">
            В неё попадают игроки минимум с пятью партиями и устоявшимся рейтингом —
            иначе новичок с парой побед оказался бы на вершине.
          </div>
        </div>
      ) : (
        <div className="stack" style={{ gap: 6 }}>
          {leaders.map((l, i) => (
            <motion.div
              key={l.userId}
              className={`rating-row${l.userId === me?.id ? " is-me" : ""}`}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: Math.min(i * 0.02, 0.25) }}
            >
              <span className={`place${i < 3 ? ` p${i + 1}` : ""}`}>{i + 1}</span>
              {l.photoUrl ? (
                <img className="avatar" src={l.photoUrl} alt="" style={{ width: 30, height: 30 }} />
              ) : (
                <span className="rating-icon">{l.tierIcon}</span>
              )}
              <span style={{ minWidth: 0, flex: 1 }}>
                <span
                  style={{
                    display: "block",
                    fontWeight: 600,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {l.name ?? "Игрок"}
                </span>
                <span className="muted small">
                  {l.tier} · {l.games} партий
                </span>
              </span>
              <b style={{ flex: "none", fontSize: 17 }}>{l.rating}</b>
            </motion.div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
